import React, { Component } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import axios from 'axios';
import moment from 'moment';
import { connect } from 'react-redux';
import customStyle from '../../../Assets/CSS/stylesheet_UHS';
import configurations from '../../../configurations';
import Loader from '../../loader';

// Carrier drives which stepper/quote this session belongs to: "Sharing Programs" => UHF, "Insurance Plans" => VERA.
const getStepperTypeFromCookies = (cookiesData) => {
    return cookiesData.actionType;
};

// applicationSubmitted is the definitive "done" signal — stepCode keeps moving past
// SUBMIT_APPLICATION (e.g. to COMPLETED) once it's submitted, so that check has to come
// first. Otherwise: not yet on Submit Application => still on an earlier step => pending.
// On Submit Application but not yet submitted => actionable (ENROLL).
const deriveEnrollmentStatus = (record) => {
    if (record.applicationSubmitted === true) return 'enrolled';
    if (record.stepCode !== 'SUBMIT_APPLICATION' && record.stepCode !== "REVIEW_CHOICES") return 'pending';
    return 'enroll';
};

// Landing screen for Cognito self-service members: lets them review their product choices
// and click ENROLL against each product before moving into Submit Application for
// Terms acceptance & e-sign. Standalone from ReviewChoice, which stays dedicated to the
// agent-driven multi-step wizard.
class EnrollmentSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaderShow: true,
            enrollmentsTab: 'enrollments',
            reviewSummaryData: null,
            familyPackageData: null,
            enrollmentStatuses: null,
            effectiveDateFormatted: null,
            firstPaymentDateFormatted: null,
            effectiveDateShort: null,
            effectiveDate: null,
            draftDayFormatted: null,
        };
    }

      componentDidMount() {
        this.fetchReviewChoicesData();
        this.fetchEnrollmentStatuses();
        this.fetchPaymentSummary();
        this.fetchFamilyPackage();
    }
    getSubId = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        return this.props.subId || cookiesData.subID || cookiesData.user_subId;
    }

    // Review & Approve only makes sense once every product's application has actually been
    // submitted — and it goes away again as soon as payment has succeeded, since there's
    // nothing left to review at that point.
    isAwaitingPayment = () => {
        const rows = this.state.enrollmentStatuses;
        if (!rows || rows.length === 0) return false;
        const allSubmitted = rows.every((row) => row.applicationSubmitted === true);
        if (!allSubmitted) return false;
        const paymentSucceeded = rows.some((row) => row.paymentStatus === 'SUCCESS' || row.paymentStatus === 'PENDING');
        return !paymentSucceeded;
    }

    // Launches the common-component-plugin's SetupPayment screen directly (loaded globally via
    // its plugin.iife.js script tag) rather than a native modal, so payment setup stays in sync
    // with the same screen used in Manage Enrollments.
    openPaymentSetup = () => {
        if (!window.loadCommonPluginScreen) {
            console.error('[CommonPlugin] loadCommonPluginScreen is not available — plugin script not loaded.');
            return;
        }
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        window.loadCommonPluginScreen(this.getSubId(), {
            type: 'SetupPayment',
            AgentID: cookiesData.brokerId,
            effectiveDate: this.state.effectiveDate,
            // The plugin screen is mounted straight to document.body, outside this component's
            // React tree, so componentDidMount never re-runs when it closes — refetch explicitly.
            actionClick: (data) => {
                console.log('payment setup done', data);
                this.fetchEnrollmentStatuses();
                this.fetchPaymentSummary();
            },
        });
    }

  

    fetchFamilyPackage = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        if (!subId || !brokerId) {
            this.setState({ familyPackageData: null });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/quote/familyPackeage/' + subId + '/ALL/' + brokerId)
            .then((response) => {
                this.setState({ familyPackageData: response.data });
            })
            .catch((error) => {
                console.log(error);
                this.setState({ familyPackageData: null });
            });
    }

    // effectiveDate/draftDay drive the "on <date>" / "on the <nth> of every month" text appended
    // to the Program Summary header — they aren't part of review-choices, so this is a separate call.
    fetchPaymentSummary = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        if (!subId) return;
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/setuppayment/paymentSummary/' + subId)
            .then((response) => {
                let data = response.data && response.data.response;
                if (!data) return;
                this.setState({
                    firstPaymentDateFormatted: data.firstPaymentDate ? moment(data.firstPaymentDate).format('MMMM Do, YYYY') : null,
                    effectiveDateFormatted: data.effectiveDate ? moment(data.effectiveDate).format('MMMM Do, YYYY') : null,
                    effectiveDateShort: data.effectiveDate ? moment(data.effectiveDate).format('MM/DD/YYYY') : null,
                    effectiveDate: data.effectiveDate || null,
                    draftDayFormatted: data.draftDay ? moment.localeData().ordinal(data.draftDay) : null,
                });
            })
            .catch((error) => console.log(error));
    }

    fetchEnrollmentStatuses = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        if (!subId || !brokerId) {
            this.setState({ enrollmentStatuses: [] });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/enrollment/stepper/product-type-wise/' + subId)
            .then((response) => {
                let records = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
                this.setState({
                    enrollmentStatuses: records.map((record) => ({
                        name: record.planName || "NA",
                        carrier: record.carrier || "NA",
                        productName: record.productName || "NA",
                        status: deriveEnrollmentStatus(record),
                        stepperType: record.stepperType,
                        paymentStatus: record.paymentStatus,
                        applicationSubmitted: record.applicationSubmitted === true,
                    })),
                });
            })
            .catch((error) => {
                console.log(error);
                this.setState({ enrollmentStatuses: [] });
            });
    }

    fetchReviewChoicesData = () => {
        let cookiesData = JSON.parse(sessionStorage.getItem('STATE_PARAM')) || {};
        let subId = this.props.subId || cookiesData.subID || cookiesData.user_subId;
        let brokerId = cookiesData.brokerId;
        let stepperType = getStepperTypeFromCookies(cookiesData);
        let type = cookiesData.actionType;
        if (!subId || !brokerId) {
            this.setState({ loaderShow: false });
            return;
        }
        axios.get(configurations.baseRootUrl + '/veraenroll/api/v1/quote/review-choices/' + subId + '/' + "ALL" + '/' + brokerId)
            .then((response) => {
                this.setState({
                    reviewSummaryData: response.data.programFees,
                    loaderShow: false,
                });
            }).catch((error) => {
                console.log(error);
                this.setState({ loaderShow: false });
            });
    }

    renderEnrollments() {
        if (!this.state.enrollmentStatuses) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>Loading...</div>;
        }
        if (this.state.enrollmentStatuses.length === 0) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
        }
        const rows = this.state.enrollmentStatuses;
        const isMobile = window.innerWidth <= 768;
        const programColumnWidth = isMobile ? '45%' : '30%';
        // All three status indicators share one fixed width/padding so their centers line up —
        // that shared center is where the dashed connector line (below) is drawn. Widened to fit
        // "E-Signature Complete", the longest label.
        const pillWidth = isMobile ? 160 : 220;
        const cellLeftPadding = 24;
        const pillBaseStyle = {
            display: 'inline-flex',
            // alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: pillWidth + 'px',
            padding: '3px 0px',
            borderRadius: '19px',
            fontWeight: '500',
            position: 'relative',
            zIndex: 1,
           
        };
        const connectorLineStyle = {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: (cellLeftPadding + pillWidth / 2) + 'px',
            borderLeft: '1px dashed #b3b3b3',
            zIndex: 0,
        };
        console.log('enrollmentStatuses', rows);    
        return (
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <div>
                    <table style={{ ...customStyle.reviewChoicesTable, tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: programColumnWidth }} />
                            <col style={{ width: (100 - parseInt(programColumnWidth, 10)) + '%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={customStyle.TabTableHeaderCell}>Program / Plan</th>
                                <th style={{...customStyle.TabTableHeaderCell, padding: "17px 66px"}}>Enrollment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ ...customStyle.reviewChoicesTableCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>{row.name}</b></td>
                                    <td style={{ ...customStyle.reviewChoicesTableCell, position: 'relative' }}>
                                        <div style={connectorLineStyle} />
                                        {row.status === 'enrolled' &&
                                            <span style={{ ...pillBaseStyle, backgroundColor: '#2fb344', color: '#fff',  justifyContent: 'flex-start', }}>
                                                <CheckIcon style={{ fontSize: '21px', marginRight: '10px', marginLeft: '7px' }} /> E-Signature Complete
                                            </span>
                                        }
                                        {row.status === 'enroll' &&
                                            <Button
                                                variant="contained"
                                                style={{ ...pillBaseStyle, backgroundColor: '#1a2c40', color: '#fff', textTransform: 'none' }}
                                                onClick={() => this.props.onEnrollClick && this.props.onEnrollClick(row)}
                                            >
                                                ENROLL
                                            </Button>
                                        }
                                        {row.status === 'pending' &&
                                            <span style={{ ...pillBaseStyle, backgroundColor: '#e0e0e0', color: '#e05353', fontWeight: "400" }}>
                                                Application Pending
                                            </span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderProgramSummary() {
        if (!this.state.reviewSummaryData) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
        }
        const { header, body } = this.state.reviewSummaryData;
        const sectionTitleStyle = { backgroundColor: '#c1c0c4', color: '#1a1a1a', fontWeight: 'bold', padding: '10px 24px', fontSize: '15px' };
        const sectionTotalCellStyle = { padding: '10px 24px', fontSize: '16px', color: '#19191d', fontWeight: 'bold', backgroundColor: '#f9f9f9' };
        // Plain (non-sticky) variant of reviewChoicesTotalCell — that token pins to the bottom
        // of the scrollable body (for a <tfoot> that must stay visible while scrolling), which
        // makes it overlap short content when used as a normal last row like this one.
        const grandTotalCellStyle = { padding: '10px 24px', fontSize: '17px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#6f6d74' };
        // The API includes the grand total as its own body entry (no rows array — just a
        // title + todayAmount/regularAmount), alongside real sections whose rows can be empty
        // when nothing's selected for that carrier yet (status: "IN_PROGRESS"). Fall back to
        // summing sections' own total rows client-side if that entry isn't present.
        const sections = body.filter((item) => Array.isArray(item.rows));
        const grandTotalEntry = body.find((item) => !Array.isArray(item.rows));
        const parseMoneyAmount = (str) => (!str || str === '-') ? 0 : (parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
        const formatMoneyAmount = (num) => '$' + num.toFixed(2);
        let grandTodayTotal = 0;
        let grandRegularTotal = 0;
        if (!grandTotalEntry) {
            sections.forEach((section) => {
                if (!section.rows.length) return;
                const totalRow = section.rows[section.rows.length - 1];
                grandTodayTotal += parseMoneyAmount(totalRow.todayAmount);
                grandRegularTotal += parseMoneyAmount(totalRow.regularAmount);
            });
        }
        return (
            <div>
                <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                    <div>
                        <table style={customStyle.reviewChoicesTable}>
                            <thead>
                                <tr>
                                    {header.map((headerText, hIndex) => (
                                        <th key={hIndex} style={customStyle.FamilyTabTableHeaderCell}>
                                            {headerText}
                                            {hIndex === 1 && this.state.firstPaymentDateFormatted && <><br />on {this.state.firstPaymentDateFormatted}</>}
                                            {hIndex === 2 && this.state.draftDayFormatted && <><br />on the {this.state.draftDayFormatted} of every month</>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sections.map((section, sectionIndex) => (
                                    <React.Fragment key={sectionIndex}>
                                        <tr>
                                            <td colSpan={header.length} style={sectionTitleStyle}>{section.title}</td>
                                        </tr>
                                        {section.rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={header.length} style={customStyle.reviewChoicesTableCell}>No selections yet.</td>
                                            </tr>
                                        ) : (
                                            <>
                                                {section.rows.slice(0, -1).map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        <td style={customStyle.reviewChoicesTableCell}>{row.text}</td>
                                                        <td style={customStyle.reviewChoicesTableCell}>{row.todayAmount}</td>
                                                        <td style={customStyle.reviewChoicesTableCell}>{row.regularAmount}</td>
                                                    </tr>
                                                ))}
                                                {(() => {
                                                    // A single-row section has no separate line items above it
                                                    // (slice(0, -1) is empty) — this row is the section's only
                                                    // content, not a bolded total, so it gets a plain white
                                                    // background instead of the #f9f9f9 "total" shading.
                                                    const lastRow = section.rows[section.rows.length - 1];
                                                    const lastRowStyle = section.rows.length === 1
                                                        ? { ...sectionTotalCellStyle, backgroundColor: '#fff', fontWeight: 'normal', fontSize: '15px' }
                                                        : sectionTotalCellStyle;
                                                    return (
                                                        <tr>
                                                            <td style={lastRowStyle}>{lastRow.text}</td>
                                                            <td style={lastRowStyle}>{lastRow.todayAmount}</td>
                                                            <td style={lastRowStyle}>{lastRow.regularAmount}</td>
                                                        </tr>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </React.Fragment>
                                ))}
                                <tr>
                                    <td style={grandTotalCellStyle}>{grandTotalEntry ? grandTotalEntry.title : 'Family Total for Complete Health Package'}</td>
                                    <td style={grandTotalCellStyle}>{grandTotalEntry ? grandTotalEntry.todayAmount : formatMoneyAmount(grandTodayTotal)}</td>
                                    <td style={grandTotalCellStyle}>{grandTotalEntry ? grandTotalEntry.regularAmount : formatMoneyAmount(grandRegularTotal)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    renderFamilyDetails() {
        // familyPackageData can come back truthy but missing `header` (e.g. an error-shaped
        // body) — guard the field itself, not just the object, before Object.keys(header).
        if (!this.state.familyPackageData || !this.state.familyPackageData.header) {
            return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
        }
        const { header, sections, grandTotal } = this.state.familyPackageData;
        const safeSections = sections || [];
        // header/rows carry one dynamic key per family member (a numeric member id) alongside
        // the fixed text/familyTotal keys — Object.keys order matches insertion order from the API.
        const memberIds = Object.keys(header).filter((key) => key !== 'text');
        const colSpan = memberIds.length + 2;
        const sectionTitleStyle = { backgroundColor: '#c1c0c4', color: '#1a1a1a', fontWeight: 'bold', padding: '10px 24px', fontSize: '15px' };
        const sectionTotalCellStyle = { padding: '10px 24px', fontSize: '16px', color: '#19191d', fontWeight: 'bold', backgroundColor: '#f9f9f9' };
        const grandTotalCellStyle = { padding: '10px 24px', fontSize: '17px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#6f6d74' };
        return (
            <div>
                <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                    <div>
                        <table style={customStyle.reviewChoicesTable}>
                            <thead>
                                <tr>
                                    <th style={customStyle.FamilyTabTableHeaderCell}>{header.text}</th>
                                    {memberIds.map((memberId) => (
                                        <th key={memberId} style={customStyle.FamilyTabTableHeaderCell}>{header[memberId]}</th>
                                    ))}
                                    <th style={customStyle.FamilyTabTableHeaderCell}>Family Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {safeSections.map((section, sectionIndex) => {
                                    const rows = section.rows || [];
                                    const lineItems = rows.filter((row) => !row.isTotal);
                                    const totalRow = rows.find((row) => row.isTotal);
                                    return (
                                        <React.Fragment key={sectionIndex}>
                                            <tr>
                                                <td colSpan={colSpan} style={sectionTitleStyle}>{section.title}</td>
                                            </tr>
                                            {lineItems.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    <td style={customStyle.reviewChoicesTableCell}>{row.text}</td>
                                                    {memberIds.map((memberId) => (
                                                        <td key={memberId} style={customStyle.reviewChoicesTableCell}>{row[memberId]}</td>
                                                    ))}
                                                    <td style={customStyle.reviewChoicesTableCell}>{row.familyTotal}</td>
                                                </tr>
                                            ))}
                                            {totalRow &&
                                                <tr>
                                                    <td style={sectionTotalCellStyle}>{totalRow.text}</td>
                                                    {memberIds.map((memberId) => (
                                                        <td key={memberId} style={sectionTotalCellStyle}>{totalRow[memberId]}</td>
                                                    ))}
                                                    <td style={sectionTotalCellStyle}>{totalRow.familyTotal}</td>
                                                </tr>
                                            }
                                        </React.Fragment>
                                    );
                                })}
                                {grandTotal &&
                                    <tr>
                                        <td style={grandTotalCellStyle}>{grandTotal.title}</td>
                                        {memberIds.map((memberId) => (
                                            <td key={memberId} style={grandTotalCellStyle}>{grandTotal[memberId]}</td>
                                        ))}
                                        <td style={grandTotalCellStyle}>{grandTotal.familyTotal}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {this.state.loaderShow ? <Loader></Loader> : ''}
                <Grid container style={{ fontFamily: 'Roboto, Arial, Helvetica, sans-serif', fontSize: '14px', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <div style={{ textAlign: 'justify', width: '95%' }}>
                                    <p>
                                      Your representative has already entered your information in the Enrollment Portal. You can review a summary of your product choices below and the family members enrolled. Once you are satisfied with the choices, you can click ENROLL against each Product. Review the terms,  indicate your acceptance of the same, and when you’re ready, click SUBMIT APPLICATION. Once all applications have been submitted, you can review and approve your payment terms. 
                                    </p>
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <div style={customStyle.reviewChoicesTabsStep} className="reviewChoicesTabsResponsive">
                                    <div
                                        style={this.state.enrollmentsTab === 'enrollments' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'enrollments' })}
                                    >ENROLLMENTS</div>
                                    <div
                                        style={this.state.enrollmentsTab === 'summary' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'summary' })}
                                    >PROGRAM SUMMARY</div>
                                    <div
                                        style={this.state.enrollmentsTab === 'family' ? customStyle.reviewChoicesTabActiveStep : customStyle.reviewChoicesTabInactiveStep}
                                        className="reviewChoicesTabResponsive"
                                        onClick={() => this.setState({ enrollmentsTab: 'family' })}
                                    >FAMILY DETAILS</div>
                                </div>
                                <div style={{ backgroundColor: '#fff' }}>
                                    {this.state.enrollmentsTab === 'enrollments'
                                        ? this.renderEnrollments()
                                        : this.state.enrollmentsTab === 'summary'
                                        ? this.renderProgramSummary()
                                        : this.renderFamilyDetails()}
                                </div>
                                {this.state.enrollmentsTab === 'enrollments' && this.isAwaitingPayment() &&
                                    <div style={{
                                        marginTop: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        backgroundColor: '#f5f5f5',
                                        padding: '10px 24px',
                                        borderRadius: '10px',
                                        width: window.innerWidth <= 768 ? '100%' : '50%',
                                        boxSizing: 'border-box',
                                    }}>
                                        <span style={{ fontWeight: '400', fontSize: '15px' }}>
                                            Review and approve your payment details and billing dates.
                                        </span>
                                        <Button
                                            variant="contained"
                                            style={{ backgroundColor: '#1a2c40', color: '#fff', textTransform: 'none', borderRadius: '20px', padding: '8px 24px' }}
                                            onClick={this.openPaymentSetup}
                                        >
                                            REVIEW & APPROVE
                                        </Button>
                                    </div>
                                }
                                {this.state.enrollmentsTab !== 'enrollments' && this.state.effectiveDateShort &&
                                    <div style={{
                                        marginTop: '20px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        backgroundColor: '#d9ecf9',
                                        color: '#3a7bbf',
                                        padding: '14px 24px',
                                        borderRadius: '10px',
                                        fontWeight: 'bold',
                                        fontSize: '15px',
                                    }}>
                                        <InfoOutlinedIcon style={{ fontSize: '22px' }} />
                                        Program Effective Date: {this.state.effectiveDateShort}
                                    </div>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        subId: state.subId,
        email: state.email,
    };
}

export default connect(mapStateToProps)(EnrollmentSummary);
